import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Trophy, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  travel: '#A2D2FF',
  skills: '#B9FBC0',
  adventure: '#FFC8DD',
  relationships: '#FF6B6B',
  health: '#FDF0D5',
  creative: '#CDB4DB',
  career: '#FFE8A3',
  other: '#E0E0E0',
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function MemoryTrophies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, completed: 0 });
  const gridRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bucket-list`, { withCredentials: true });
      const completed = (data.items || []).filter((i) => i.completed).sort((a, b) => {
        return (b.completed_at || '').localeCompare(a.completed_at || '');
      });
      setItems(completed);
      setStats(data.stats || { total: 0, completed: 0 });
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!gridRef.current || items.length === 0) return;
    const node = gridRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = node.querySelectorAll('.trophy-card-anim');
            Array.from(cards).forEach((card, idx) => {
              card.style.animationDelay = `${idx * 70}ms`;
              card.classList.add('in-view');
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [items]);

  if (loading) return null;

  return (
    <div className="neo-card p-5 bg-[#FFFAF0] dark:bg-[#1f1f3a]" data-testid="memory-trophies">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-xl font-bold font-['Fredoka'] flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" fill="currentColor" /> Memory Trophies
          </h3>
          <p className="text-xs opacity-75 mt-0.5">Every dream you've lived ✨</p>
        </div>
        <span className="text-sm font-bold bg-[#FDF0D5] px-3 py-1 rounded-full border-2 border-black">
          {stats.completed} / {stats.total} unlocked
        </span>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 opacity-70">
          <Lock className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm font-semibold">No trophies yet</p>
          <p className="text-xs mt-1">Head to the Bucket tab, add a dream, and check it off to earn your first trophy 🏆</p>
        </div>
      ) : (
        <div ref={gridRef} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {items.map((item) => {
            const bg = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other;
            return (
              <div
                key={item.id}
                className="trophy-card-anim relative neo-card p-3 text-center transition-transform hover:-translate-y-1"
                style={{ background: `linear-gradient(135deg, ${bg} 0%, #FFF 120%)` }}
                data-testid={`trophy-${item.id}`}
              >
                {/* Ribbon */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-yellow-400 text-[9px] font-bold px-2 py-0.5 rounded-full border-2 border-black">
                  UNLOCKED
                </div>
                {/* Emoji */}
                <div
                  className="w-14 h-14 mx-auto mt-2 rounded-full border-[3px] border-black flex items-center justify-center text-3xl shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
                  style={{ backgroundColor: '#FDF0D5' }}
                >
                  {item.emoji || '🏆'}
                </div>
                <p className="mt-2 text-sm font-bold font-['Fredoka'] line-clamp-2 min-h-[2.5em]">
                  {item.title}
                </p>
                <p className="text-[10px] opacity-75 mt-1">{formatDate(item.completed_at)}</p>
                {item.reflection && (
                  <p className="mt-1 text-[10px] italic opacity-70 line-clamp-2" title={item.reflection}>
                    💭 {item.reflection}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
